/**
 * useTimeWatchData — hook centralizzato per TUTTI i dati economici e temporali
 * del TIME Watch.
 *
 * Fonte di verità:
 *   • Utente autenticato → MongoDB via /api/user-data (payload.timewatch)
 *   • Utente anonimo    → localStorage (chiave `time-watch:data`) come fallback
 *
 * Migrazione automatica dal vecchio formato:
 *   • `time-watch:accounts` (oggetto {portafoglio, mps, unicredit, postepay, paypal})
 *   • `time-watch:dob`, `time-watch:tempo-dato`, `time-watch:tempo-avuto`
 *
 * Sync al login: se il cloud è vuoto ma il localStorage ha dati → upload.
 *
 * Salvataggio: debounced 700ms → PUT /api/user-data (cache localStorage sempre
 * aggiornata immediatamente).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchUserData, patchUserData } from "./userDataApi";

const LS_KEY_ANON = "time-watch:data";
const LS_KEY_PREFIX_USER = "time-watch:data:user:";
const LEGACY_ACCOUNTS_KEY = "time-watch:accounts";
const LEGACY_DOB_KEY = "time-watch:dob";
const LEGACY_DATO_KEY = "time-watch:tempo-dato";
const LEGACY_AVUTO_KEY = "time-watch:tempo-avuto";

// Sprint isolamento multiaccount:
// La chiave del localStorage viene sempre determinata a partire dall'utente
// autenticato. In assenza di utente si usa LS_KEY_ANON. In questo modo i dati
// di due account diversi che accedono dallo stesso browser non si mescolano
// mai — ogni account legge/scrive esclusivamente la propria cache locale.
function lsKeyFor(userId) {
  if (userId) return `${LS_KEY_PREFIX_USER}${userId}`;
  return LS_KEY_ANON;
}

// Legacy account keys → default labels for migration.
const LEGACY_ACCOUNT_LABELS = {
  portafoglio: "Portafoglio",
  mps: "Monte dei Paschi",
  unicredit: "UniCredit",
  postepay: "Postepay Evolution",
  paypal: "PayPal",
};

function safeUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function defaultAccounts() {
  return [
    { id: safeUuid(), label: "Portafoglio", balance: 0 },
    { id: safeUuid(), label: "Monte dei Paschi", balance: 0 },
    { id: safeUuid(), label: "UniCredit", balance: 0 },
    { id: safeUuid(), label: "Postepay Evolution", balance: 0 },
    { id: safeUuid(), label: "PayPal", balance: 0 },
  ];
}

function defaultData() {
  return {
    accounts: defaultAccounts(),
    transactions: [],
    dob: "",
    tempoDato: 0,
    tempoAvuto: 0,
    version: 1,
  };
}

function normalise(data) {
  const base = defaultData();
  if (!data || typeof data !== "object") return base;
  const accounts = Array.isArray(data.accounts) && data.accounts.length >= 1
    ? data.accounts
        .slice(0, 5)
        .map((a) => ({
          id: a?.id || safeUuid(),
          label: typeof a?.label === "string" && a.label.trim() ? a.label : "Fonte",
          balance: Number.isFinite(Number(a?.balance)) ? Number(a.balance) : 0,
        }))
    : base.accounts;
  // pad to always have exactly 5 accounts
  while (accounts.length < 5) {
    accounts.push({ id: safeUuid(), label: `Fonte ${accounts.length + 1}`, balance: 0 });
  }
  return {
    accounts,
    transactions: Array.isArray(data.transactions) ? data.transactions : [],
    dob: typeof data.dob === "string" ? data.dob : "",
    tempoDato: Number(data.tempoDato) || 0,
    tempoAvuto: Number(data.tempoAvuto) || 0,
    version: 1,
  };
}

/**
 * Try to build initial data from legacy localStorage keys (pre-Sprint-2).
 * Returns null if nothing migrateable.
 */
function migrateFromLegacy() {
  try {
    const legacyAccountsRaw = window.localStorage.getItem(LEGACY_ACCOUNTS_KEY);
    const legacyDobRaw = window.localStorage.getItem(LEGACY_DOB_KEY);
    const legacyDatoRaw = window.localStorage.getItem(LEGACY_DATO_KEY);
    const legacyAvutoRaw = window.localStorage.getItem(LEGACY_AVUTO_KEY);
    if (
      !legacyAccountsRaw &&
      !legacyDobRaw &&
      !legacyDatoRaw &&
      !legacyAvutoRaw
    ) {
      return null;
    }
    const legacyAccounts = legacyAccountsRaw ? JSON.parse(legacyAccountsRaw) : null;
    const accounts = legacyAccounts
      ? Object.keys(LEGACY_ACCOUNT_LABELS).map((k) => ({
          id: safeUuid(),
          label: LEGACY_ACCOUNT_LABELS[k],
          balance: Number(legacyAccounts[k]) || 0,
        }))
      : defaultAccounts();
    return {
      accounts,
      transactions: [],
      dob: legacyDobRaw ? JSON.parse(legacyDobRaw) : "",
      tempoDato: legacyDatoRaw ? Number(JSON.parse(legacyDatoRaw)) || 0 : 0,
      tempoAvuto: legacyAvutoRaw ? Number(JSON.parse(legacyAvutoRaw)) || 0 : 0,
      version: 1,
    };
  } catch {
    return null;
  }
}

function loadLocal(userId) {
  const key = lsKeyFor(userId);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) return normalise(JSON.parse(raw));
  } catch {
    /* fallthrough */
  }
  // Migrazione legacy: SOLO per la cache anonima. Per un utente autenticato
  // che non ha ancora un cache locale isolata, si parte SEMPRE da vuoto per
  // evitare di ereditare dati appartenuti ad un altro account che ha usato
  // in precedenza lo stesso dispositivo.
  if (!userId) {
    const migrated = migrateFromLegacy();
    if (migrated) {
      try {
        window.localStorage.setItem(key, JSON.stringify(migrated));
      } catch {
        /* ignore */
      }
      return normalise(migrated);
    }
  }
  const fresh = defaultData();
  try {
    window.localStorage.setItem(key, JSON.stringify(fresh));
  } catch {
    /* ignore */
  }
  return fresh;
}

function saveLocal(userId, data) {
  const key = lsKeyFor(userId);
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function useTimeWatchData() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = isAuthenticated && user && user.id ? user.id : null;
  // In-memory state parte SEMPRE dai default per evitare di mostrare la
  // cache di un altro utente durante il boot. Verrà popolato dal loadLocal
  // scoped-user (o dal cloud) all'interno del useEffect qui sotto.
  const [data, setDataInternal] = useState(() => defaultData());
  const [cloudLoaded, setCloudLoaded] = useState(false);
  const saveTimer = useRef(null);
  const skipNextSave = useRef(true); // avoid initial mount save
  const currentUserRef = useRef(userId);

  // Reset immediato quando l'utente cambia (login, logout, cambio account).
  // Questo evita che la UI mostri anche solo per un frame dati appartenenti
  // al precedente account, durante la fetch del cloud del nuovo utente.
  useEffect(() => {
    if (currentUserRef.current === userId) return;
    currentUserRef.current = userId;
    skipNextSave.current = true;
    // Carica dalla cache LS SCOPED al nuovo utente (o anonima). Se non esiste
    // (es. nuovo account, o account che accede da nuovo dispositivo), parte
    // dai valori di default — mai dalla cache di un altro account.
    setDataInternal(loadLocal(userId));
    setCloudLoaded(false);
  }, [userId]);

  // Load from cloud once authenticated
  useEffect(() => {
    let mounted = true;
    if (authLoading) return;
    if (!isAuthenticated) {
      setCloudLoaded(false);
      // In modalità anonima, allineiamo lo stato alla cache anon.
      skipNextSave.current = true;
      setDataInternal(loadLocal(null));
      return;
    }
    // Snapshot dell'ID utente al momento della fetch, così se durante il
    // fetch l'utente cambia di nuovo, scartiamo la risposta stale.
    const uidAtFetch = userId;
    (async () => {
      try {
        const cloud = await fetchUserData();
        const cloudTw = cloud?.timewatch;
        if (!mounted) return;
        // Se nel frattempo l'utente è cambiato, scarta questa risposta.
        if (currentUserRef.current !== uidAtFetch) return;
        if (cloudTw && cloudTw.accounts && cloudTw.accounts.length) {
          // Cloud wins: cache locale scoped-user aggiornata al cloud.
          skipNextSave.current = true;
          const normalised = normalise(cloudTw);
          setDataInternal(normalised);
          saveLocal(uidAtFetch, normalised);
        } else {
          // Cloud vuoto: NON si fa più auto-upload di dati locali (che
          // potrebbero appartenere ad un altro account che ha usato in
          // precedenza lo stesso dispositivo). L'utente parte con i valori
          // di default e potrà popolare la sua Attività TIME Watch dall'app.
          skipNextSave.current = true;
          const fresh = defaultData();
          setDataInternal(fresh);
          saveLocal(uidAtFetch, fresh);
        }
        if (currentUserRef.current === uidAtFetch) setCloudLoaded(true);
      } catch {
        /* offline / not authenticated → keep local */
        if (currentUserRef.current === uidAtFetch) setCloudLoaded(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, userId]);

  // Ascolta l'evento di rollover mensile per ricaricare i dati dal cloud
  // subito dopo che `tempoDato`/`tempoAvuto` sono stati azzerati lato server.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = async () => {
      const uidAtFetch = currentUserRef.current;
      try {
        const cloud = await fetchUserData();
        if (currentUserRef.current !== uidAtFetch) return;
        const cloudTw = cloud?.timewatch;
        if (cloudTw && cloudTw.accounts && cloudTw.accounts.length) {
          skipNextSave.current = true;
          const normalised = normalise(cloudTw);
          setDataInternal(normalised);
          saveLocal(uidAtFetch, normalised);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("ti:rollover-done", handler);
    return () => window.removeEventListener("ti:rollover-done", handler);
  }, [isAuthenticated]);

  // Persist on every change: always local (scoped-user) + (debounced) cloud
  useEffect(() => {
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    saveLocal(userId, data);
    if (!isAuthenticated) return;
    // Guardia anti race-condition: non emettere PATCH cloud finché il primo
    // fetch dei dati remoti non è completato. Evita che lo stato di boot
    // temporaneo (defaultData con nuovi UUID / cache locale precedente)
    // possa sovrascrivere il payload storico appena caricato dal server.
    if (!cloudLoaded) return;
    const uidAtSave = userId;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Doppio-check dell'utente prima di scrivere sul cloud: se nel frattempo
      // è cambiato, non facciamo overwrite di dati che appartengono ad altri.
      if (currentUserRef.current !== uidAtSave) return;
      patchUserData("timewatch", data).catch(() => {
        /* ignore transient network errors — cache holds the truth */
      });
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [data, isAuthenticated, userId, cloudLoaded]);

  // === Derived values ===
  const totalEuro = useMemo(
    () => data.accounts.reduce((acc, a) => acc + (Number(a.balance) || 0), 0),
    [data.accounts]
  );
  const speseTotali = useMemo(
    () =>
      data.transactions
        .filter((t) => t.type === "out")
        .reduce((acc, t) => acc + (Number(t.amount) || 0), 0),
    [data.transactions]
  );
  const entrateTotali = useMemo(
    () =>
      data.transactions
        .filter((t) => t.type === "in")
        .reduce((acc, t) => acc + (Number(t.amount) || 0), 0),
    [data.transactions]
  );

  // === Mutators ===
  const setData = useCallback((updater) => {
    setDataInternal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return normalise(next);
    });
  }, []);

  const renameAccount = useCallback((id, label) => {
    setData((prev) => ({
      ...prev,
      accounts: prev.accounts.map((a) =>
        a.id === id ? { ...a, label: label.trim().slice(0, 40) || a.label } : a
      ),
    }));
  }, [setData]);

  const renameAccounts = useCallback((labels) => {
    // labels: array of strings aligned to accounts order
    setData((prev) => ({
      ...prev,
      accounts: prev.accounts.map((a, i) => ({
        ...a,
        label: typeof labels[i] === "string" && labels[i].trim()
          ? labels[i].trim().slice(0, 40)
          : a.label,
      })),
    }));
  }, [setData]);

  /**
   * Register a movement.
   *
   * Supported types:
   *   • "in"     → entrata su un conto (accountId)
   *   • "out"    → spesa da un conto (accountId)
   *   • "switch" → trasferimento interno tra due conti
   *                (fromAccountId → toAccountId). Il Patrimonio Totale
   *                non cambia; viene registrato come UN SOLO movimento.
   *
   * Il saldo può andare in negativo (uscita/switch > saldo). Additivo:
   * la logica per "in" e "out" resta identica a prima.
   */
  const addTransaction = useCallback(
    ({ type, accountId, fromAccountId, toAccountId, amount, date, note }) => {
      const amt = Math.max(0, Math.floor(Number(amount) || 0));
      if (!amt) return false;
      const iso = date ? new Date(date).toISOString() : new Date().toISOString();
      const safeNote = typeof note === "string" ? note.slice(0, 120) : "";

      if (type === "in" || type === "out") {
        const delta = type === "in" ? amt : -amt;
        const tx = {
          id: safeUuid(),
          date: iso,
          type,
          accountId,
          amount: amt,
          note: safeNote,
        };
        setData((prev) => {
          const accounts = prev.accounts.map((a) =>
            a.id === accountId ? { ...a, balance: (Number(a.balance) || 0) + delta } : a
          );
          return {
            ...prev,
            accounts,
            transactions: [tx, ...prev.transactions],
          };
        });
        return true;
      }

      if (type === "switch") {
        if (!fromAccountId || !toAccountId || fromAccountId === toAccountId) {
          return false;
        }
        const tx = {
          id: safeUuid(),
          date: iso,
          type: "switch",
          fromAccountId,
          toAccountId,
          amount: amt,
          note: safeNote,
        };
        setData((prev) => {
          const accounts = prev.accounts.map((a) => {
            if (a.id === fromAccountId) {
              return { ...a, balance: (Number(a.balance) || 0) - amt };
            }
            if (a.id === toAccountId) {
              return { ...a, balance: (Number(a.balance) || 0) + amt };
            }
            return a;
          });
          return {
            ...prev,
            accounts,
            transactions: [tx, ...prev.transactions],
          };
        });
        return true;
      }

      return false;
    },
    [setData]
  );

  /**
   * Delete a transaction by id. Reverses its effect on the account balance(s).
   * Supporta anche i movimenti "switch" (ripristina entrambi i conti).
   */
  const deleteTransaction = useCallback((txId) => {
    setData((prev) => {
      const tx = prev.transactions.find((t) => t.id === txId);
      if (!tx) return prev;
      let accounts = prev.accounts;
      if (tx.type === "switch") {
        accounts = prev.accounts.map((a) => {
          if (a.id === tx.fromAccountId) {
            return { ...a, balance: (Number(a.balance) || 0) + tx.amount };
          }
          if (a.id === tx.toAccountId) {
            return { ...a, balance: (Number(a.balance) || 0) - tx.amount };
          }
          return a;
        });
      } else {
        const delta = tx.type === "in" ? -tx.amount : tx.amount;
        accounts = prev.accounts.map((a) =>
          a.id === tx.accountId
            ? { ...a, balance: (Number(a.balance) || 0) + delta }
            : a
        );
      }
      return {
        ...prev,
        accounts,
        transactions: prev.transactions.filter((t) => t.id !== txId),
      };
    });
  }, [setData]);

  /**
   * updateTransactionNote — modifica ESCLUSIVAMENTE la causale (`note`) di
   * una transazione già esistente. Non altera saldi, tipo, importo, data,
   * accountId né alcun altro campo strutturale. Nessuna transazione nuova
   * viene creata; nessun record viene duplicato.
   */
  const updateTransactionNote = useCallback((txId, newNote) => {
    const safeNote =
      typeof newNote === "string" ? newNote.slice(0, 120) : "";
    setData((prev) => {
      let found = false;
      const transactions = prev.transactions.map((t) => {
        if (t.id !== txId) return t;
        found = true;
        return { ...t, note: safeNote };
      });
      if (!found) return prev;
      return { ...prev, transactions };
    });
  }, [setData]);

  const setDob = useCallback(
    (dob) => setData((prev) => ({ ...prev, dob: dob || "" })),
    [setData]
  );

  const setTempoDato = useCallback(
    (updater) =>
      setData((prev) => ({
        ...prev,
        tempoDato:
          typeof updater === "function"
            ? Math.max(0, Math.floor(updater(prev.tempoDato) || 0))
            : Math.max(0, Math.floor(Number(updater) || 0)),
      })),
    [setData]
  );

  const setTempoAvuto = useCallback(
    (updater) =>
      setData((prev) => ({
        ...prev,
        tempoAvuto:
          typeof updater === "function"
            ? Math.max(0, Math.floor(updater(prev.tempoAvuto) || 0))
            : Math.max(0, Math.floor(Number(updater) || 0)),
      })),
    [setData]
  );

  return {
    // state
    accounts: data.accounts,
    transactions: data.transactions,
    dob: data.dob,
    tempoDato: data.tempoDato,
    tempoAvuto: data.tempoAvuto,
    // derived
    totalEuro,
    speseTotali,
    entrateTotali,
    // meta
    isAuthenticated,
    cloudLoaded,
    // mutators
    renameAccount,
    renameAccounts,
    addTransaction,
    deleteTransaction,
    updateTransactionNote,
    setDob,
    setTempoDato,
    setTempoAvuto,
  };
}
